import { apiClient } from './client';
import type {
  AddDeviceResult,
  AddedDevicesResponse,
  RemoveAddedDeviceResult,
  AssignReplacedAssetResult,
  RemoveReplacedAssetResult,
  ReplaceableAssetsResponse,
  HardwareRequestApprovalStatus,
  HardwareRequestDecisionResult,
  HardwareRequestDetailResponse,
  HardwareRequestInternalNote,
  HardwareRequestInternalNotesResponse,
  RemoveInternalNoteResult,
  PostInternalNoteResult,
  DecidePurchaseResult,
  EscalatePurchaseResult,
  NoticeRecipient,
  NoticeRecipientsResponse,
  PurchaseApprovalResponse,
  PurchaseEnvelope,
  PurchaseNoticeResponse,
  SendPurchaseNoticeResult,
  RequestedLinesResponse,
  RestoreLineResult,
  WithdrawLineResult,
  HardwareRequestSummary,
  HardwareRequestTicketCheck,
  HardwareRequestTicketLinkResult,
  FulfilmentKind,
  HardwareRequestFulfilmentResponse,
  SourcingNoteOutcome,
  StockOptionsResponse,
} from '@/types/oscar';

/**
 * Typed wrappers over `/api/hardware-requests*` — the hardware request
 * registry (Phase 1 of `OnsiteCore-hardware-lifecycle-design.md`).
 *
 * Read only. Nothing here approves, allocates or writes; approval is Phase 2
 * and arrives with the `oscar-asset-manager` role.
 *
 * ⛔ Gated server-side on admin + helpdesk, which is NARROWER than Oscar's
 * usual read set. A request carries the requester's delivery address and, on a
 * lost/stolen diversion, a number that may be personal — finance and reviewer
 * roles have no business reading either.
 */

export interface HardwareRequestFilters {
  /** 'New' | 'Replacement'. Empty for both. */
  requestType?: string;
  /**
   * Pending | Approved | Declined | Referred, or 'all'. Empty means all.
   *
   * ⚠ 'Pending' is resolved server-side to "no decision row" — it is not a
   * stored value, and the API knows that. Do not translate it here.
   */
  status?: HardwareRequestApprovalStatus | 'all';
  /** Matches employee, submitter, devices or branch. */
  search?: string;
  take?: number;
}

/** GET /api/hardware-requests — the registry, newest first. */
export function listHardwareRequests(
  filters: HardwareRequestFilters = {},
  signal?: AbortSignal,
): Promise<HardwareRequestSummary[]> {
  const q = new URLSearchParams();
  if (filters.requestType) q.set('requestType', filters.requestType);
  if (filters.status) q.set('status', filters.status);
  if (filters.search) q.set('search', filters.search);
  if (filters.take) q.set('take', String(filters.take));
  const qs = q.toString();
  return apiClient.get<HardwareRequestSummary[]>(
    `/api/hardware-requests${qs ? `?${qs}` : ''}`,
    signal,
  );
}

/**
 * GET /api/hardware-requests/:id — one request, plus every asset it says it is
 * replacing with Oscar's own record of each (age, warranty, Intune check-in).
 */
export function getHardwareRequest(
  id: number,
  signal?: AbortSignal,
): Promise<HardwareRequestDetailResponse> {
  return apiClient.get<HardwareRequestDetailResponse>(
    `/api/hardware-requests/${id}`,
    signal,
  );
}

/**
 * POST /api/hardware-requests/:id/decision — approve, decline or refer back.
 *
 * ⛔ Needs `oscar-asset-manager` or `oscar-admin`, NOT helpdesk: deciding is a
 * commitment to spend. Enforced server-side; `canApproveHardware` only hides UI.
 *
 * ⛔ Declining or referring REQUIRES a reason — the API refuses without one and
 * so does the CHECK constraint behind it. Approving may be silent.
 *
 * ⚠ This records a decision and nothing else. It allocates nothing — that is
 * a separate act, once approved (see allocateHardwareRequestFulfilment) — and
 * raises no purchase (Phase 5).
 *
 * ⚠ It DOES reach the Zendesk ticket: the write-back went live 30/09/2026
 * (Phase 2b). An earlier version of this note said it did not.
 */
export function decideHardwareRequest(
  id: number,
  status: Exclude<HardwareRequestApprovalStatus, 'Pending'>,
  notes?: string,
): Promise<HardwareRequestDecisionResult> {
  return apiClient.post<HardwareRequestDecisionResult>(
    `/api/hardware-requests/${id}/decision`,
    { Status: status, Notes: notes ?? null },
  );
}

/**
 * POST /api/hardware-requests/:id/decision/undo — withdraw a decision, returning
 * the request to pending. **Admin only**, narrower than making it: the person
 * who decided should not be able to quietly rewrite it.
 */
export function undoHardwareRequestDecision(
  id: number,
): Promise<HardwareRequestDecisionResult> {
  return apiClient.post<HardwareRequestDecisionResult>(
    `/api/hardware-requests/${id}/decision/undo`,
  );
}

/**
 * POST /api/hardware-requests/:id/decision/retry-zendesk — send the decision to
 * the ticket again after a failed write-back.
 *
 * ⚠ A button rather than a background job, deliberately: a retry loop hides a
 * persistent failure behind noise, where a button somebody has pressed six times
 * says the problem needs a person.
 *
 * Needs admin or helpdesk — retrying only re-sends what was already decided, so
 * it needs no more authority than fulfilling the work does.
 */
export function retryHardwareRequestZendesk(
  id: number,
): Promise<HardwareRequestDecisionResult> {
  return apiClient.post<HardwareRequestDecisionResult>(
    `/api/hardware-requests/${id}/decision/retry-zendesk`,
  );
}

// ── Ticket re-link (093) ─────────────────────────────────────────────────────

/**
 * GET /api/hardware-requests/:id/ticket-link/check?ticket=N — what Zendesk says
 * about a ticket, WITHOUT linking it.
 *
 * ⚠ Reads only, so any Oscar role may call it. Linking is admin. The point is
 * to say "that one is closed too" before somebody commits a redirection.
 */
export function checkHardwareRequestTicket(
  id: number,
  ticket: number,
  signal?: AbortSignal,
): Promise<HardwareRequestTicketCheck> {
  return apiClient.get<HardwareRequestTicketCheck>(
    `/api/hardware-requests/${id}/ticket-link/check?ticket=${ticket}`,
    signal,
  );
}

/**
 * POST /api/hardware-requests/:id/ticket-link — send this request's decisions
 * and sourcing notes to a different ticket from now on.
 *
 * ⛔ Admin only, and the reason is required. Re-pointing changes where a
 * decision is published and who can see it, and it is the kind of thing
 * somebody asks about six months later.
 *
 * ⚠ The server asks Zendesk before it writes the row — a ticket that is closed,
 * or does not exist, is refused and nothing is stored.
 */
export function linkHardwareRequestTicket(
  id: number,
  zendeskTicketId: number,
  reason: string,
): Promise<HardwareRequestTicketLinkResult> {
  return apiClient.post<HardwareRequestTicketLinkResult>(
    `/api/hardware-requests/${id}/ticket-link`,
    { ZendeskTicketId: zendeskTicketId, Reason: reason },
  );
}

/**
 * DELETE /api/hardware-requests/:id/ticket-link — withdraw the link; decisions
 * go back to the ticket the request was raised on.
 *
 * ⚠ Soft on the server: the withdrawn row stays, so "somebody linked X and then
 * thought better of it" remains readable.
 */
export function unlinkHardwareRequestTicket(
  id: number,
): Promise<HardwareRequestTicketLinkResult> {
  return apiClient.delete<HardwareRequestTicketLinkResult>(
    `/api/hardware-requests/${id}/ticket-link`,
  );
}

// ── Fulfilment (Phase 4) ─────────────────────────────────────────────────────

/**
 * GET /api/hardware-requests/:id/fulfilment — everything allocated against one
 * request.
 *
 * ⚠ Cancelled rows are INCLUDED, unlike the registry's counts which exclude
 * them. A screen showing only live rows cannot answer "what happened to the
 * laptop that was reserved last week?", which is the question cancel exists to
 * leave an answer to.
 */
export function listHardwareRequestFulfilment(
  id: number,
  signal?: AbortSignal,
): Promise<HardwareRequestFulfilmentResponse> {
  return apiClient.get<HardwareRequestFulfilmentResponse>(
    `/api/hardware-requests/${id}/fulfilment`, signal);
}

/**
 * POST /api/hardware-requests/:id/fulfilment — reserve an asset, or a quantity
 * of stock, against an APPROVED request.
 *
 * ⛔ For Stock this checks the quantity OUT immediately, so it leaves
 * availability at the moment it is reserved. For an Asset it records the link
 * and deliberately does NOT change the asset's lifecycle state — a reservation
 * is not worth destroying "Spare / Ready to Deploy" over.
 */
export function allocateHardwareRequestFulfilment(
  id: number,
  body: {
    Kind: FulfilmentKind; AssetId?: number; StockItemId?: number; Qty?: number;
    ProjectCostId?: number; Amount?: number; Notes?: string | null;
    /**
     * Which project to charge (084). Omitted means the standing one - or, when the approver drew the
     * purchase on a CEA envelope, the envelope's project. Naming a different one is refused.
     */
    ProjectReference?: string;
    /**
     * Who it is being bought from — a SupplierId from the shared creditor
     * master. Required when Oscar raises the line, because the Project Tracker
     * refuses a capex cost without a vendor; refused when recording a cost line
     * that already exists, since that line names its own.
     */
    VendorSupplierId?: number;
    /**
     * When the purchase is expected to be PAID (yyyy-mm-dd). Required when
     * Oscar raises the line: a cost has no date of its own, and without a
     * payment row it never lands in a financial month.
     */
    ExpectedPaymentDate?: string;
    // ⛔ NO APPROVAL FIELD. The spend is approved by the IT Finance Approver
    // (purchase-approval), and the server reads that from the database; the
    // allocator's own tick (098) is gone.
  },
): Promise<{ FulfilmentId: number; Allocated: boolean; EnvelopeWarning: string | null } & SourcingNoteOutcome> {
  return apiClient.post<{ FulfilmentId: number; Allocated: boolean; EnvelopeWarning: string | null } & SourcingNoteOutcome>(
    `/api/hardware-requests/${id}/fulfilment`, body);
}

/**
 * POST /api/hardware-requests/fulfilment/:fulfilmentId/issue — it reached the
 * person.
 *
 * ⚠ `Assigned` false is NOT a failure: a BRANCH request has no person to assign
 * to, and nothing yet maps a branch code to an Oscar location. The device is
 * issued either way and assigned by hand afterwards.
 */
export function issueHardwareRequestFulfilment(
  fulfilmentId: number,
  assetId?: number,
): Promise<{ Issued: boolean; Assigned: boolean; AssetId: number | null }> {
  return apiClient.post<{ Issued: boolean; Assigned: boolean; AssetId: number | null }>(
    `/api/hardware-requests/fulfilment/${fulfilmentId}/issue`,
    assetId ? { AssetId: assetId } : undefined);
}

/**
 * GET /api/hardware-requests/:id/stock-options — what can satisfy THIS request.
 *
 * ⛔ TWO LISTS, ONE READ. `Options` is what the fulfiller can pick. `Devices` is
 * one row per device ON the request whether or not anything answers it, because
 * the approver's question is "can we meet this from stock?" and three different
 * answers to it look identical if you only return the pickable lines.
 *
 * ⚠ Lines with nothing on hand are still returned in `Options`. "We hold none of
 * those" is a different answer from "there is no such thing".
 */
export function listHardwareRequestStockOptions(
  id: number,
  signal?: AbortSignal,
): Promise<StockOptionsResponse> {
  return apiClient.get<StockOptionsResponse>(`/api/hardware-requests/${id}/stock-options`, signal);
}

/**
 * POST /api/hardware-requests/fulfilment/:fulfilmentId/cancel — give the
 * reservation up. A reason is required; for Stock the quantity goes back.
 */
export function cancelHardwareRequestFulfilment(
  fulfilmentId: number,
  reason: string,
): Promise<{ Cancelled: boolean } & SourcingNoteOutcome> {
  return apiClient.post<{ Cancelled: boolean } & SourcingNoteOutcome>(
    `/api/hardware-requests/fulfilment/${fulfilmentId}/cancel`, { Reason: reason });
}

/**
 * POST /api/hardware-requests/fulfilment/:fulfilmentId/retry-zendesk — send the
 * SOURCING note to the ticket again after it failed (089).
 *
 * ⛔ A button, not a background job. A retry loop hides a persistent failure
 * behind noise; a button somebody has pressed six times says the problem is not
 * transient and needs a person.
 *
 * ⚠ It re-sends the SAME note. The text is built from the stored allocation, so
 * a retry cannot say something different from the attempt it is retrying.
 */
export function retryHardwareRequestSourcingNote(
  fulfilmentId: number,
): Promise<{ FulfilmentId: number } & SourcingNoteOutcome> {
  return apiClient.post<{ FulfilmentId: number } & SourcingNoteOutcome>(
    `/api/hardware-requests/fulfilment/${fulfilmentId}/retry-zendesk`);
}

// ── The device being replaced (oscar migration 122) ──────────────────────────

/**
 * GET /api/hardware-requests/:id/replaceable-assets - what the request's
 * beneficiary holds in Oscar right now, for an approver to name the device being
 * replaced.
 *
 * ⛔ Approver only (oscar-asset-manager or oscar-admin), reading included: a
 * list of what a named person holds is not for everyone who can open the page.
 *
 * ⚠ `Resolved: false` is an answer, not an error - Oscar could not tell who the
 * request is for - so it arrives as a 200 with a reason.
 */
export function listReplaceableAssets(
  id: number,
  signal?: AbortSignal,
): Promise<ReplaceableAssetsResponse> {
  return apiClient.get<ReplaceableAssetsResponse>(
    `/api/hardware-requests/${id}/replaceable-assets`,
    signal,
  );
}

/**
 * POST /api/hardware-requests/:id/replaced-assets - the approver names one asset
 * as the device being replaced.
 *
 * ⛔ Sends the ASSET only. Whose kit it is comes from the request, read on the
 * server; a caller who could say would be able to move anybody's asset.
 *
 * ⚠ Marks the asset Replacement Requested, as the form does at submit. It is
 * refused for a request that is not a replacement, or was declined.
 */
export function assignReplacedAsset(
  id: number,
  assetId: number,
): Promise<AssignReplacedAssetResult> {
  return apiClient.post<AssignReplacedAssetResult>(
    `/api/hardware-requests/${id}/replaced-assets`,
    { AssetId: assetId },
  );
}

/**
 * DELETE /api/hardware-requests/:id/replaced-assets/:assetId - take an approver's
 * assignment back.
 *
 * ⛔ Only what an APPROVER assigned. What the requester named stays as they gave
 * it; asking to remove it is refused with the reason.
 *
 * ⚠ Soft on the server, and the asset's state is put back only if this
 * assignment moved it and nothing has moved it since.
 */
export function removeReplacedAsset(
  id: number,
  assetId: number,
): Promise<RemoveReplacedAssetResult> {
  return apiClient.delete<RemoveReplacedAssetResult>(
    `/api/hardware-requests/${id}/replaced-assets/${assetId}`,
  );
}

// ── Devices an approver adds (oscar migration 123) ───────────────────────────

/**
 * GET /api/hardware-requests/:id/added-devices - what an approver has added to
 * this request, and what they could still add.
 *
 * ⛔ Approver only (oscar-asset-manager or oscar-admin), reading included.
 *
 * ⚠ Refused for a declined request, with the reason: there is nothing to add to.
 */
export function listAddedDevices(
  id: number,
  signal?: AbortSignal,
): Promise<AddedDevicesResponse> {
  return apiClient.get<AddedDevicesResponse>(`/api/hardware-requests/${id}/added-devices`, signal);
}

/**
 * POST /api/hardware-requests/:id/added-devices - the approver adds one catalogue
 * device to the request.
 *
 * ⛔ Sends an ID and an optional note, never a name: the name is a snapshot taken
 * from the catalogue on the server.
 *
 * ⚠ Refused for a device on the wrong side of the location/person partition, for
 * the "no device needed" option, and for one already on the request.
 */
export function addDeviceToRequest(
  id: number,
  deviceOptionId: number,
  note?: string,
): Promise<AddDeviceResult> {
  return apiClient.post<AddDeviceResult>(`/api/hardware-requests/${id}/added-devices`, {
    DeviceOptionId: deviceOptionId,
    ...(note ? { Note: note } : {}),
  });
}

/**
 * DELETE /api/hardware-requests/:id/added-devices/:deviceOptionId - take a device
 * an approver added back off.
 *
 * ⛔ Only what an APPROVER added. What the requester ticked stays as they asked;
 * asking to remove it is refused with the reason.
 */
export function removeAddedDevice(
  id: number,
  deviceOptionId: number,
): Promise<RemoveAddedDeviceResult> {
  return apiClient.delete<RemoveAddedDeviceResult>(
    `/api/hardware-requests/${id}/added-devices/${deviceOptionId}`,
  );
}

// ── Internal notes (OnsiteCore-oscar-HardwareRequestInternalNote.sql) ────────

/**
 * GET /api/hardware-requests/:id/internal-notes - the approvers' working notes,
 * newest first.
 *
 * ⛔ Approver only (oscar-asset-manager or oscar-admin), reading included. These
 * are never sent to Zendesk and never shown to the requester.
 */
export function listHardwareRequestInternalNotes(
  id: number,
  signal?: AbortSignal,
): Promise<HardwareRequestInternalNotesResponse> {
  return apiClient.get<HardwareRequestInternalNotesResponse>(
    `/api/hardware-requests/${id}/internal-notes`,
    signal,
  );
}

/**
 * POST /api/hardware-requests/:id/internal-notes - append a note. The author and
 * the time are stamped on the server; the body can name neither.
 */
export function addHardwareRequestInternalNote(
  id: number,
  body: string,
): Promise<HardwareRequestInternalNote> {
  return apiClient.post<HardwareRequestInternalNote>(`/api/hardware-requests/${id}/internal-notes`, {
    Body: body,
  });
}

/**
 * POST /api/hardware-requests/:id/internal-notes/:noteId/post-to-zendesk - put ONE note on the
 * request's Zendesk ticket as a PRIVATE (internal) comment.
 *
 * ⛔ An explicit act, never a side effect of saving, and never public. Once only: a comment cannot
 * be taken back off the ticket from here, so a second press is refused. `Posted: false` carries
 * Zendesk's reason; the note itself is already saved.
 */
export function postHardwareRequestInternalNoteToZendesk(
  id: number,
  noteId: number,
): Promise<PostInternalNoteResult> {
  return apiClient.post<PostInternalNoteResult>(
    `/api/hardware-requests/${id}/internal-notes/${noteId}/post-to-zendesk`,
    {},
  );
}

/**
 * DELETE /api/hardware-requests/:id/internal-notes/:noteId - take a note back.
 * Its author or an admin only; the row is kept, with who removed it.
 */
export function removeHardwareRequestInternalNote(
  id: number,
  noteId: number,
): Promise<RemoveInternalNoteResult> {
  return apiClient.delete<RemoveInternalNoteResult>(
    `/api/hardware-requests/${id}/internal-notes/${noteId}`,
  );
}

// ── Withdrawing a pick the requester made in error ───────────────────────────

/**
 * GET /api/hardware-requests/:id/requested-lines - what the requester picked,
 * with what an approver has withdrawn, and whether that can still be changed.
 *
 * ⛔ Approver only (oscar-asset-manager or oscar-admin), reading included.
 */
export function listRequestedLines(
  id: number,
  signal?: AbortSignal,
): Promise<RequestedLinesResponse> {
  return apiClient.get<RequestedLinesResponse>(`/api/hardware-requests/${id}/requested-lines`, signal);
}

/**
 * POST /api/hardware-requests/:id/withdrawn-lines - withdraw one of the
 * requester's picks.
 *
 * ⛔ Names the pick by kind and id (or by name where the line predates the ids);
 * the stored name is read from the request on the server. A reason is required:
 * the requester is told what was removed and why when the request is approved.
 *
 * ⚠ Refused once the request is Approved or Declined, and for the last item left.
 */
export function withdrawRequestLine(
  id: number,
  line: { LineKind: 'Device' | 'Accessory'; OptionId: number | null; LineName: string },
  reason: string,
): Promise<WithdrawLineResult> {
  return apiClient.post<WithdrawLineResult>(`/api/hardware-requests/${id}/withdrawn-lines`, {
    LineKind: line.LineKind,
    ...(line.OptionId ? { OptionId: line.OptionId } : { LineName: line.LineName }),
    Reason: reason,
  });
}

/**
 * DELETE /api/hardware-requests/:id/withdrawn-lines/:withdrawnLineId - put a
 * withdrawn pick back. The withdrawal row is kept, with who put it back.
 */
export function restoreRequestLine(
  id: number,
  withdrawnLineId: number,
): Promise<RestoreLineResult> {
  return apiClient.delete<RestoreLineResult>(
    `/api/hardware-requests/${id}/withdrawn-lines/${withdrawnLineId}`,
  );
}

// ── Purchase notice ──────────────────────────────────────────────────────────

/**
 * GET /api/hardware-requests/:id/purchase-notice - what an approved request needs
 * buying, whether the email about it went, and how many people are on the list.
 *
 * ⛔ Approver only (oscar-asset-manager or oscar-admin), reading included.
 */
export function getPurchaseNotice(
  id: number,
  signal?: AbortSignal,
): Promise<PurchaseNoticeResponse> {
  return apiClient.get<PurchaseNoticeResponse>(`/api/hardware-requests/${id}/purchase-notice`, signal);
}

/**
 * POST /api/hardware-requests/:id/purchase-notice - send the notice now, again if
 * it already went. Stamped with who pressed it.
 *
 * ⚠ Refused for a request that is not approved, one with nothing to buy, and when
 * nobody is on the list - each with the reason.
 */
export function sendPurchaseNotice(id: number): Promise<SendPurchaseNoticeResult> {
  return apiClient.post<SendPurchaseNoticeResult>(`/api/hardware-requests/${id}/purchase-notice`, {});
}

/** GET /api/hardware-notification-recipients - who is emailed when a purchase is needed. */
export function listNoticeRecipients(signal?: AbortSignal): Promise<NoticeRecipientsResponse> {
  return apiClient.get<NoticeRecipientsResponse>('/api/hardware-notification-recipients', signal);
}

/**
 * POST /api/hardware-notification-recipients - add a person. Admin only.
 *
 * ⛔ Company addresses only; anything else is refused with the reason.
 */
export function addNoticeRecipient(email: string): Promise<NoticeRecipient> {
  return apiClient.post<NoticeRecipient>('/api/hardware-notification-recipients', { Email: email });
}

/** DELETE /api/hardware-notification-recipients/:id - take a person off. Admin only; soft delete. */
export function removeNoticeRecipient(id: number): Promise<{ RecipientId: number; Removed: boolean }> {
  return apiClient.delete<{ RecipientId: number; Removed: boolean }>(
    `/api/hardware-notification-recipients/${id}`,
  );
}

// ── Purchase approval (escalation) ───────────────────────────────────────────

/**
 * GET /api/hardware-requests/:id/purchase-approval - where the purchase is, and what
 * THIS caller can do about it.
 *
 * Asset manager, finance approver or admin. The Can... flags are the server's
 * answer for this caller; the page cannot know who raised the escalation.
 */
export function getPurchaseApproval(
  id: number,
  signal?: AbortSignal,
): Promise<PurchaseApprovalResponse> {
  return apiClient.get<PurchaseApprovalResponse>(`/api/hardware-requests/${id}/purchase-approval`, signal);
}

/**
 * POST /api/hardware-requests/:id/purchase-approval - ask for approval to buy what
 * has no stock. Asset manager or admin; the person asking is stamped.
 *
 * Refused until the request is approved, and when everything is in stock. The
 * email to the people who decide goes after it commits and cannot undo it.
 */
export function requestPurchaseApproval(id: number, note?: string): Promise<EscalatePurchaseResult> {
  return apiClient.post<EscalatePurchaseResult>(
    `/api/hardware-requests/${id}/purchase-approval`,
    note ? { Note: note } : {},
  );
}

/**
 * POST /api/hardware-requests/:id/purchase-approval/decision - approve or decline
 * the spend. Finance approver or admin, and never the person who asked.
 *
 * A decline needs a reason. Internal - the requester and the ticket are not told.
 */
export function decidePurchaseApproval(
  id: number,
  decision: 'Approved' | 'Declined',
  notes?: string,
  envelope?: { ProjectForecastId: number } | { NoEnvelope: true },
): Promise<DecidePurchaseResult> {
  return apiClient.post<DecidePurchaseResult>(`/api/hardware-requests/${id}/purchase-approval/decision`, {
    Decision: decision,
    ...(notes ? { Notes: notes } : {}),
    // ⛔ AN APPROVAL NAMES AN ENVELOPE OR SAYS THERE IS NONE - the server refuses silence, and a
    // decline names neither.
    ...(decision === 'Approved' ? envelope : {}),
  });
}

/**
 * GET /api/purchase-envelopes - the CEA envelopes a purchase may draw on, with their LIVE approved /
 * drawn / left. Finance approver or admin. Only envelopes on the GL account and cost centre Oscar
 * raises purchases on are listed, because only those would cover a purchase.
 */
export function getPurchaseEnvelopes(signal?: AbortSignal): Promise<PurchaseEnvelope[]> {
  return apiClient.get<PurchaseEnvelope[]>('/api/purchase-envelopes', signal);
}

/** DELETE /api/hardware-requests/:id/purchase-approval - take back an undecided escalation. */
export function withdrawPurchaseApproval(id: number): Promise<{ SubmissionId: number; Withdrawn: boolean }> {
  return apiClient.delete<{ SubmissionId: number; Withdrawn: boolean }>(
    `/api/hardware-requests/${id}/purchase-approval`,
  );
}
